// Row model + source-chip mapping for an asetusarvo (context-setpoint) Signaalit row (story
// #1515). Every context-setpoint quantity family an equipment's KB type declares gets exactly
// one row in Konfiguraatio -> Signaalit and Datan laatu -> Signaalit, never a separate
// panel — see docs/spec/knowledge-base.md § Context setpoints — suggestion and accept. Pure:
// the TSX (SignaalitList) only renders what this module computes.

import type { CardTone } from "@/lib/card-styles";
import type {
  ContextSetpoint,
  ContextSetpointEffectiveSource,
} from "@/lib/api/building";

// `effective_source` -> the Finnish source chip shown on the row. `unwired` is the neutral
// not-yet-set state, distinct from a resolved source (device/accepted/manual).
export const CONTEXT_SETPOINT_CHIP_LABEL: Record<
  ContextSetpointEffectiveSource,
  string
> = {
  device: "laite",
  accepted: "opittu",
  manual: "käyttäjä",
  unwired: "ei asetettu",
};

export function contextSetpointChipLabel(
  source: ContextSetpointEffectiveSource,
): string {
  return CONTEXT_SETPOINT_CHIP_LABEL[source];
}

// `effective_source` -> the card-tone the chip renders with (`@/lib/card-styles`). A resolved
// source reads as ok (green); unwired stays the neutral empty tone, never warn — an unfilled
// asetusarvo is expected on a fresh building, not a fault.
export const CONTEXT_SETPOINT_CHIP_TONE: Record<
  ContextSetpointEffectiveSource,
  CardTone
> = {
  device: "ok",
  accepted: "ok",
  manual: "ok",
  unwired: "empty",
};

export function contextSetpointChipTone(
  source: ContextSetpointEffectiveSource,
): CardTone {
  return CONTEXT_SETPOINT_CHIP_TONE[source];
}

// A family's Finnish name, keyed by its id — the label fallback for a family that merges into
// no existing signal row (unmapped: there is no device row to already carry a label).
export function contextSetpointLabels(
  context_setpoints: readonly ContextSetpoint[],
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const cs of context_setpoints) {
    labels[cs.context_setpoint_id] = cs.name_fi;
  }
  return labels;
}

// One context-setpoint family placed onto its Signaalit home.
export interface ContextSetpointRowPlacement {
  context_setpoint_id: string;
  name_fi: string;
  unit: string;
  device_present: boolean;
  suggested_value: number | null;
  suggested_unit: string | null;
  effective_source: ContextSetpointEffectiveSource;
  effective_value: number | null;
  // True when the family's device data point already has a signal row on this equipment
  // (`existing_row_names`) — the caller decorates that row with the chip and must NOT add a
  // second, synthetic row for the same family (the "no duplicate row" contract).
  merges_with_existing_row: boolean;
}

// `existing_row_names` is the set of signal names already rendered for this equipment (its
// device data points) — a family's `context_setpoint_id` equals its device data point id when
// one is declared, so membership decides merge vs. synthetic placement.
export function placeContextSetpointRows(
  context_setpoints: readonly ContextSetpoint[],
  existing_row_names: ReadonlySet<string>,
): ContextSetpointRowPlacement[] {
  return context_setpoints.map((cs) => ({
    context_setpoint_id: cs.context_setpoint_id,
    name_fi: cs.name_fi,
    unit: cs.unit,
    device_present: cs.device_present,
    suggested_value: cs.suggested_value,
    suggested_unit: cs.suggested_unit,
    effective_source: cs.effective_source,
    effective_value: cs.effective_value,
    merges_with_existing_row: existing_row_names.has(cs.context_setpoint_id),
  }));
}

// Which write controls a context-setpoint row offers — Konfiguraatio only, never Datan
// laatu (that lens stays read-only). A mapped (device_present) family offers neither: the
// device always wins, so an accept/typed-fill control would be dead UI. An unmapped family always
// allows typing a value; it additionally offers accept only when a learned suggestion is ready
// (`suggested_value` non-null — the KB tick withholds it otherwise, docs/spec/knowledge-base.md
// § Context setpoints — suggestion and accept → "Surfaced only when...").
export interface ContextSetpointControlEligibility {
  can_accept: boolean;
  can_type_value: boolean;
}

export function contextSetpointControlEligibility(
  placement: Pick<
    ContextSetpointRowPlacement,
    "device_present" | "suggested_value"
  >,
): ContextSetpointControlEligibility {
  if (placement.device_present) {
    return { can_accept: false, can_type_value: false };
  }
  return {
    can_accept: placement.suggested_value != null,
    can_type_value: true,
  };
}
